class ProductGallery487 extends HTMLElement {
  static observedAttributes = ['color', 'clientWidth'];

  constructor() {
    super();
    this.observeSelectedVariantAttribute();
  }

  connectedCallback() {
    var comp = this;
    this.elements = {
      prevButton: comp.querySelector('.prev-button') ?? null,
      nextButton: comp.querySelector('.next-button') ?? null,
      dotsContainer: comp.querySelector('.dots-container') ?? null,
    };

    this.options = {
      axis: 'x',
      container: `[data-track]`,
      slides: `[data-slide]`,
      loop: false,
    };

    this.option_thumbs = {
      containScroll: 'keepSnaps',
      dragFree: true,
    };

    this.viewportNodeMainCarousel = this.querySelector('.main-viewport');
    this.viewportNodeThumbCarousel = this.querySelector('.thumbnail-viewport');

    this.init();
    this.setupDots();
    this.handleInitialVariantSelection();
  }

  observeSelectedVariantAttribute() {
    const closestElement = this.closest('[data-product-card-selected_variant_id]');
    
    if (!closestElement) {
      console.error("No ancestor element found with 'data-product-card-selected_variant_id' attribute.");
      return;
    }

    const observer = new MutationObserver(mutationsList => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-product-card-selected_variant_id') {
          const newValue = closestElement.getAttribute('data-product-card-selected_variant_id');
          if (newValue) {
            this.scrollToSlide(newValue);
          }
        }
      }
    });

    observer.observe(closestElement, { attributes: true });
  }

  handleInitialVariantSelection() {
    const closestElement = this.closest('[data-product-card-selected_variant_id]');
    if (!closestElement) {
      console.warn("No ancestor element found with 'data-product-card-selected_variant_id' attribute for initial variant selection.");
      return;
    }

    const initialVariantId = closestElement.getAttribute('data-product-card-selected_variant_id');
    if (initialVariantId) {
      this.scrollToSlide(initialVariantId);
    }
  }

  scrollToSlide(selectedVariantId) {
    const targetSlideIndex = this.findSlideIndexByVariantId(selectedVariantId);
    if (targetSlideIndex !== -1) {
      this.emblaApiMain.scrollTo(targetSlideIndex, false);
      this.emblaApiThumb.scrollTo(targetSlideIndex, false);
    }
  }

  findSlideIndexByVariantId(variantId) {
    const slides = this.emblaApiMain.slideNodes();
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const associatedVariantId = slide.dataset.targetVariant;
      if (associatedVariantId && associatedVariantId === variantId) {
        return i;
      }
    }
    return -1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    console.log(`Attribute ${name} has changed.`);
  }

  destroy() {
    this.emblaApiMain.destroy();
    this.emblaApiThumb.destroy();
  }

  init() {
    this.emblaApiMain = EmblaCarousel(
      this.viewportNodeMainCarousel,
      this.options
    );
    this.emblaApiThumb = EmblaCarousel(this.viewportNodeThumbCarousel, {
      ...this.options,
      ...this.option_thumbs,
    });

    const removeThumbBtnsClickHandlers = this.addThumbBtnsClickHandlers();
    const removeToggleThumbBtnsActive = this.addToggleThumbBtnsActive();
    const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
      this.emblaApiMain,
      this.elements.prevButton,
      this.elements.nextButton
    );

    this.emblaApiMain.on('select', () => this.updateDots());

    this.emblaApiMain
      .on('destroy', removeThumbBtnsClickHandlers)
      .on('destroy', removeToggleThumbBtnsActive);

    this.emblaApiThumb
      .on('destroy', removeThumbBtnsClickHandlers)
      .on('destroy', removeToggleThumbBtnsActive);
  }

  addThumbBtnsClickHandlers() {
    const slidesThumbs = this.emblaApiThumb.slideNodes();

    const scrollToIndex = slidesThumbs.map((_, index) => () => {
      this.emblaApiMain.scrollTo(index, false);
    });

    slidesThumbs.forEach((slideNode, index) => {
      slideNode.addEventListener('click', scrollToIndex[index], false);
    });

    return () => {
      slidesThumbs.forEach((slideNode, index) => {
        slideNode.removeEventListener('click', scrollToIndex[index], false);
      });
    };
  }

  setupDots() {
    const dotsContainer = this.elements.dotsContainer;
    if (!dotsContainer) return;

    const slides = this.emblaApiMain.slideNodes();

    if(slides.length <= 1){
      dotsContainer.classList.add("!hidden");
      return;
    }

    dotsContainer.innerHTML = ""; // Clear any existing dots

    slides.forEach((_, index) => {
        const dot = document.createElement("button");
        dot.className = "dot";
        dot.addEventListener("click", () => this.emblaApiMain.scrollTo(index));
        dotsContainer.appendChild(dot);
    });

    this.updateDots();
  }

  updateDots() {
    const dotsContainer = this.elements.dotsContainer;
    if (!dotsContainer) return;

    const selectedIndex = this.emblaApiMain.selectedScrollSnap();
    dotsContainer.querySelectorAll(".dot").forEach((dot, index) => {
        dot.classList.toggle("active", index === selectedIndex);
    });
  }

  addToggleThumbBtnsActive() {
    const slidesThumbs = this.emblaApiThumb.slideNodes();

    const toggleThumbBtnsState = () => {
      const selected = this.emblaApiMain.selectedScrollSnap();
      const previous = this.emblaApiMain.previousScrollSnap();
      this.emblaApiThumb.scrollTo(selected);
      slidesThumbs[previous].classList.remove('selected');
      slidesThumbs[selected].classList.add('selected');
    };

    this.emblaApiMain.on('select', toggleThumbBtnsState);
    this.emblaApiThumb.on('init', toggleThumbBtnsState);

    return () => {
      const selected = this.emblaApiMain.selectedScrollSnap();
      slidesThumbs[selected].classList.remove('selected');
    };
  }

  addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
    const togglePrevNextBtnsState = () => {
      if (emblaApi.canScrollPrev()) prevBtn.removeAttribute('disabled');
      else prevBtn.setAttribute('disabled', 'disabled');

      if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled');
      else nextBtn.setAttribute('disabled', 'disabled');
    };

    emblaApi
      .on('select', togglePrevNextBtnsState)
      .on('init', togglePrevNextBtnsState)
      .on('reInit', togglePrevNextBtnsState);

    return () => {
      prevBtn.removeAttribute('disabled');
      nextBtn.removeAttribute('disabled');
    };
  };

  addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
    const scrollPrev = () => emblaApi.scrollPrev();
    const scrollNext = () => emblaApi.scrollNext();
    prevBtn.addEventListener('click', scrollPrev, false);
    nextBtn.addEventListener('click', scrollNext, false);

    const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
      emblaApi,
      prevBtn,
      nextBtn
    );

    return () => {
      removeTogglePrevNextBtnsActive();
      prevBtn.removeEventListener('click', scrollPrev, false);
      nextBtn.removeEventListener('click', scrollNext, false);
    };
  };
}

customElements.define('product-gallery-487', ProductGallery487);